// src/pages/PoolAdmin.js — 음식풀 관리 (관리자 전용): 지역·포지션 필터 · 추가 · 잘어울림 · 삭제
import React, { useCallback, useEffect, useState } from 'react';
import {
  deleteRecommendFood, getCodeSearch, getRecommendFoodList, getRecommendFoodListCount,
  getRecommendGoodWithList, postRecommendFoodList, updateRecommendGoodWith,
} from '../api/dietApi';
import FoodPicker from '../components/ui/FoodPicker';
import Pagination from '../components/ui/Pagination';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import { MacroText, fmt } from '../components/ui/Nutrients';
import { useToast } from '../components/ui/Toast';

const LIMIT = 10;
const GOOD_WITH_MAX = 5;

export default function PoolAdmin() {
  const toast = useToast();
  const [regions, setRegions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [rg, setRg] = useState('');
  const [pos, setPos] = useState('');
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [adding, setAdding] = useState(false);          // 추가: 음식 고르기
  const [addTarget, setAddTarget] = useState(null);     // 추가: 지역·포지션 선택
  const [addRg, setAddRg] = useState('KR');
  const [addPos, setAddPos] = useState('main');
  const [matching, setMatching] = useState(null);       // 잘어울림 편집 대상
  const [matchInitial, setMatchInitial] = useState([]);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    getCodeSearch('foodRegionSe').then((r) => setRegions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getCodeSearch('foodPos').then((r) => setPositions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true); setError('');
    Promise.all([getRecommendFoodList(query, rg, pos, (page - 1) * LIMIT), getRecommendFoodListCount(query, rg, pos)])
      .then(([l, c]) => { setItems(Array.isArray(l.data) ? l.data : []); setTotal(Number(c.data) || 0); })
      .catch((e) => { setItems([]); setTotal(0); setError(e?.response?.data?.message || '목록을 불러오지 못했습니다.'); })
      .finally(() => setLoading(false));
  }, [query, rg, pos, page]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      await postRecommendFoodList(addTarget.foodCd, addRg, addPos);
      toast(`${addTarget.foodNm}을(를) 음식풀에 추가했습니다.`, { type: 'success' });
      setAddTarget(null);
      load();
    } catch (e) {
      toast(e?.response?.data?.message || '추가 중 오류가 발생했습니다.', { type: 'error' });
    }
  };

  const openMatch = async (f) => {
    try {
      const r = await getRecommendGoodWithList(f.foodRcmdSeq);
      setMatchInitial((Array.isArray(r.data) ? r.data : []).filter((x) => x.foodCd));
    } catch { setMatchInitial([]); }
    setMatching(f);
  };

  const saveMatch = async (foods) => {
    try {
      await updateRecommendGoodWith(matching.foodRcmdSeq, foods.map((x) => x.foodCd).join(','));
      toast('잘 어울리는 음식을 저장했습니다.', { type: 'success' });
      setMatching(null);
      load();
    } catch (e) {
      toast(e?.response?.data?.message || '저장 중 오류가 발생했습니다.', { type: 'error' });
    }
  };

  const remove = async () => {
    const f = deleting;
    try {
      await deleteRecommendFood(f.foodRcmdSeq);
      toast(`${f.foodNm}을(를) 음식풀에서 뺐습니다.`, { type: 'success' });
      setDeleting(null);
      if (items.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (e) {
      toast(e?.response?.data?.message || '삭제 중 오류가 발생했습니다.', { type: 'error' });
    }
  };

  return (
    <div className="pool">
      <div className="pool-filters">
        <select className="select" value={rg} onChange={(e) => { setRg(e.target.value); setPage(1); }}>
          <option value="">지역 전체</option>
          {regions.map((r) => <option key={r.code} value={r.code}>{r.codeNm}</option>)}
        </select>
        <select className="select" value={pos} onChange={(e) => { setPos(e.target.value); setPage(1); }}>
          <option value="">포지션 전체</option>
          {positions.map((p) => <option key={p.code} value={p.code}>{p.codeNm}</option>)}
        </select>
        <form className="pool-search" onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(keyword.trim()); }}>
          <input className="input" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="음식 이름" />
          <button className="btn" type="submit">검색</button>
        </form>
        <button className="btn primary" type="button" onClick={() => setAdding(true)}>+ 음식 추가</button>
      </div>

      {!loading && !error && <p className="pool-count">{total.toLocaleString('ko-KR')}개</p>}
      {loading ? <p className="rc-msg">불러오는 중…</p>
        : error ? <p className="rc-msg error">{error}</p>
          : !items.length ? <EmptyState message="조건에 맞는 음식이 없습니다." />
            : (
              <>
                {items.map((f) => (
                  <div key={f.foodRcmdSeq} className="pool-row">
                    <span className="rc-pos">{f.foodRgSeNm} · {f.foodPosNm}</span>
                    <div className="pool-main">
                      <strong>{f.foodNm}</strong>
                      <span className="pool-gw">잘어울림: {f.goodWith || '미설정'}</span>
                    </div>
                    <span className="rc-kcal">{fmt(f.kcal)} kcal</span>
                    <MacroText carbo={f.carbo} protein={f.protein} fat={f.fat} />
                    <button className="btn ghost" type="button" onClick={() => openMatch(f)}>잘어울림</button>
                    <button className="icon-btn" type="button" aria-label={`${f.foodNm} 삭제`} onClick={() => setDeleting(f)}>🗑</button>
                  </div>
                ))}
                <Pagination page={page} total={total} pageSize={LIMIT} onChange={setPage} />
              </>
            )}

      <FoodPicker
        open={adding}
        title="음식풀에 추가할 음식"
        confirmLabel="다음"
        onClose={() => setAdding(false)}
        onPick={(food) => { setAdding(false); setAddTarget(food); }}
      />

      <Modal
        open={!!addTarget}
        title="지역·포지션 선택"
        size="sm"
        onClose={() => setAddTarget(null)}
        footer={(
          <>
            <button className="btn ghost" type="button" onClick={() => setAddTarget(null)}>취소</button>
            <button className="btn primary" type="button" onClick={add}>추가</button>
          </>
        )}
      >
        <p className="confirm-text"><strong>{addTarget?.foodNm}</strong></p>
        <div className="pool-add">
          <select className="select" value={addRg} onChange={(e) => setAddRg(e.target.value)}>
            {regions.map((r) => <option key={r.code} value={r.code}>{r.codeNm}</option>)}
          </select>
          <select className="select" value={addPos} onChange={(e) => setAddPos(e.target.value)}>
            {positions.map((p) => <option key={p.code} value={p.code}>{p.codeNm}</option>)}
          </select>
        </div>
      </Modal>

      <FoodPicker
        open={!!matching}
        mode="multi"
        max={GOOD_WITH_MAX}
        initial={matchInitial}
        title={`${matching?.foodNm || ''} — 잘 어울리는 음식 (최대 ${GOOD_WITH_MAX}개)`}
        confirmLabel="저장"
        onClose={() => setMatching(null)}
        onApply={saveMatch}
      />

      <Modal
        open={!!deleting}
        title="음식풀에서 빼기"
        size="sm"
        onClose={() => setDeleting(null)}
        footer={(
          <>
            <button className="btn ghost" type="button" onClick={() => setDeleting(null)}>취소</button>
            <button className="btn danger" type="button" onClick={remove}>빼기</button>
          </>
        )}
      >
        <p className="confirm-text"><strong>{deleting?.foodNm}</strong>을(를) 음식풀에서 뺄까요? 모든 사용자의 추천 후보에서 빠집니다.</p>
      </Modal>
    </div>
  );
}
